import { addDays, lastDayOfMonth } from 'date-fns';
// ---------------------------------------------------------------------------
// Custom Error
// ---------------------------------------------------------------------------
export class PaymentTermsParseError extends Error {
    constructor(message) {
        super(message);
        this.name = 'PaymentTermsParseError';
    }
}
// ---------------------------------------------------------------------------
// Regex patterns (case-insensitive, anchored)
// ---------------------------------------------------------------------------
/** Standard: "2/10 Net 30", "2%/10, Net 30" */
const STANDARD_RE = /^(\d+(?:\.\d+)?)%?\s*\/\s*(\d+)\s*[,\s]+net\s+(\d+)$/i;
/** EOM discount: "2/EOM Net 30" */
const EOM_DISCOUNT_RE = /^(\d+(?:\.\d+)?)%?\s*\/\s*eom\s*[,\s]+net\s+(\d+)$/i;
/** EOM due date: "2/10, Net EOM" */
const EOM_DUE_RE = /^(\d+(?:\.\d+)?)%?\s*\/\s*(\d+)\s*[,\s]+net\s+eom$/i;
/** Percent-days: "2% 10 Net 30", "2% 10 Days Net 30" */
const PERCENT_DAYS_RE = /^(\d+(?:\.\d+)?)\s*(?:%|percent)\s*(\d+)\s*(?:days?)?\s*[,\s]*net\s+(\d+)$/i;
/** Within + Net: "3% within 7 days Net 30" */
const WITHIN_NET_RE = /^(\d+(?:\.\d+)?)\s*(?:%|percent)?\s*(?:if\s+)?(?:paid\s+)?within\s+(\d+)\s*days?\s+net\s+(\d+)$/i;
/** Within (no net): "3% if paid within 7 days" */
const WITHIN_RE = /^(\d+(?:\.\d+)?)\s*(?:%|percent)?\s*(?:if\s+)?(?:paid\s+)?within\s+(\d+)\s*days?$/i;
/** Net numeric: "Net 30", "N30" */
const NET_NUMERIC_RE = /^n(?:et)?\s*(\d+)$/i;
/** Net EOM: "Net EOM" */
const NET_EOM_RE = /^net\s+eom$/i;
/** Immediate: "Due on Receipt", "Due Upon Receipt", "COD" */
const IMMEDIATE_RE = /^(?:due\s+(?:on|upon)\s+receipt|cod)$/i;
/** Days-only: "30 days", "45 days net" */
const DAYS_ONLY_RE = /^(\d+)\s*days?\s*(?:net)?$/i;
const ALL_PATTERNS = [
    STANDARD_RE, EOM_DISCOUNT_RE, EOM_DUE_RE, PERCENT_DAYS_RE,
    WITHIN_NET_RE, WITHIN_RE, NET_NUMERIC_RE, NET_EOM_RE,
    IMMEDIATE_RE, DAYS_ONLY_RE,
];
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function normalize(input) {
    return input.trim().replace(/\s+/g, ' ');
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Resolves a payment-terms string + invoice date into concrete dates.
 *
 * @throws {PaymentTermsParseError} when the term string is empty or
 *   doesn't match any supported format.
 */
export function resolvePaymentTerms(termString, invoiceDate) {
    if (!termString || !termString.trim()) {
        throw new PaymentTermsParseError('Term string is empty');
    }
    const raw = termString.trim();
    const norm = normalize(termString);
    // Standard: X/Y Net Z
    let m = norm.match(STANDARD_RE);
    if (m) {
        return {
            discountPercent: parseFloat(m[1]),
            discountExpirationDate: addDays(invoiceDate, parseInt(m[2], 10)),
            dueDate: addDays(invoiceDate, parseInt(m[3], 10)),
            rawTerm: raw,
        };
    }
    // EOM discount: X/EOM Net Z
    m = norm.match(EOM_DISCOUNT_RE);
    if (m) {
        return {
            discountPercent: parseFloat(m[1]),
            discountExpirationDate: lastDayOfMonth(invoiceDate),
            dueDate: addDays(invoiceDate, parseInt(m[2], 10)),
            rawTerm: raw,
        };
    }
    // EOM due: X/Y Net EOM
    m = norm.match(EOM_DUE_RE);
    if (m) {
        return {
            discountPercent: parseFloat(m[1]),
            discountExpirationDate: addDays(invoiceDate, parseInt(m[2], 10)),
            dueDate: lastDayOfMonth(invoiceDate),
            rawTerm: raw,
        };
    }
    // Percent-days: "2% 10 Net 30", "2% 10 Days Net 30"
    m = norm.match(PERCENT_DAYS_RE);
    if (m) {
        return {
            discountPercent: parseFloat(m[1]),
            discountExpirationDate: addDays(invoiceDate, parseInt(m[2], 10)),
            dueDate: addDays(invoiceDate, parseInt(m[3], 10)),
            rawTerm: raw,
        };
    }
    // Within + Net: "3% within 7 days Net 30"
    m = norm.match(WITHIN_NET_RE);
    if (m) {
        return {
            discountPercent: parseFloat(m[1]),
            discountExpirationDate: addDays(invoiceDate, parseInt(m[2], 10)),
            dueDate: addDays(invoiceDate, parseInt(m[3], 10)),
            rawTerm: raw,
        };
    }
    // Within (no net): "3% if paid within 7 days"
    m = norm.match(WITHIN_RE);
    if (m) {
        const days = parseInt(m[2], 10);
        return {
            discountPercent: parseFloat(m[1]),
            discountExpirationDate: addDays(invoiceDate, days),
            dueDate: addDays(invoiceDate, days),
            rawTerm: raw,
        };
    }
    // Net numeric: Net Z
    m = norm.match(NET_NUMERIC_RE);
    if (m) {
        return {
            discountPercent: 0,
            discountExpirationDate: invoiceDate,
            dueDate: addDays(invoiceDate, parseInt(m[1], 10)),
            rawTerm: raw,
        };
    }
    // Net EOM
    if (NET_EOM_RE.test(norm)) {
        return {
            discountPercent: 0,
            discountExpirationDate: invoiceDate,
            dueDate: lastDayOfMonth(invoiceDate),
            rawTerm: raw,
        };
    }
    // Immediate: "Due on Receipt", "Due Upon Receipt", "COD"
    if (IMMEDIATE_RE.test(norm)) {
        return {
            discountPercent: 0,
            discountExpirationDate: invoiceDate,
            dueDate: invoiceDate,
            rawTerm: raw,
        };
    }
    // Days-only: "30 days", "45 days net"
    m = norm.match(DAYS_ONLY_RE);
    if (m) {
        return {
            discountPercent: 0,
            discountExpirationDate: invoiceDate,
            dueDate: addDays(invoiceDate, parseInt(m[1], 10)),
            rawTerm: raw,
        };
    }
    throw new PaymentTermsParseError(`Unrecognized payment term: "${raw}". Supported formats: "2/10 Net 30", "2/EOM Net 30", "2/10 Net EOM", "2% 10 Net 30", "3% within 7 days", "Net 30", "N30", "Net EOM", "Due on Receipt", "COD", "30 days"`);
}
/**
 * Returns `true` when `termString` matches one of the supported
 * payment-term formats. Never throws.
 */
export function isValidPaymentTerm(termString) {
    if (!termString || !termString.trim())
        return false;
    const norm = normalize(termString);
    return ALL_PATTERNS.some((re) => re.test(norm));
}
//# sourceMappingURL=paymentTermsService.js.map